// SelectOrganization.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
} from "@mui/material";
import { getAllOrgWithDetail } from "../apis/apiList/organizationApi";
import { onboardLinkedinOrg } from "../apis/apiList/linkedinApi";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { useMessage } from "../context/MessageContext";


const SelectOrganization = () => {
  const [orgList, setOrgList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState({
    organizationId: "",   // 👈 new field
    organizationName: "",
    street: "",
    city: "",
    zip: "",
    nickName: "",
  });
  const [isNewOrg, setIsNewOrg] = useState(false);
  const [errors, setErrors] = useState({});
  const [isContinuing, setIsContinuing] = useState(false);
  const dropdownRef = useRef(null);
  const { userDetails, login } = useApp();
  const navigate = useNavigate();
  const { showMessage } = useMessage();

  // const urlParams = new URLSearchParams(window.location.search);
  // const userId = urlParams.get("userId");

  useEffect(() => {
    async function fetchOrganizations() {
      const res = await getAllOrgWithDetail();
      setOrgList(res?.data || []);
      setFilteredList(res?.data || []);
    }
    fetchOrganizations();
  }, []);

  // Handle outside click to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;

    // If user modifies the input manually -> remove orgId
    setOrgDetails((prev) => ({
      ...prev,
      organizationName: value,
      organizationId: "",   // 👈 clear id if edited
    }));

    if (value.trim() === "") {
      setFilteredList(orgList);
      setShowDropdown(false);
      return;
    }

    const filtered = orgList.filter((org) =>
      org.organizationName.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredList(filtered);
    setShowDropdown(true);
    setIsNewOrg(true);
    setSelectedOrg(null);
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    setIsNewOrg(false);
    setOrgDetails({
      organizationId: org._id,   // 👈 save id
      organizationName: org.organizationName,
      street: org.street || "",
      city: org.city || "",
      zip: org.zip || "",
      nickName: org.nickName || "",
    });
    setShowDropdown(false);
  };

  const validateFields = () => {
    let tempErrors = {};
    if (!orgDetails.organizationName.trim()) {
      tempErrors.organizationName = "Organization name is required.";
    }
    if (!orgDetails.street.trim()) {
      tempErrors.street = "Street is required.";
    }
    if (!orgDetails.city.trim()) {
      tempErrors.city = "City is required.";
    }
    if (!orgDetails.zip.trim()) {
      tempErrors.zip = "Zip Code is required.";
    } else if (!/^\d{5,6}$/.test(orgDetails.zip)) {
      tempErrors.zip = "Enter a valid Zip Code.";
    }
    if (!orgDetails.nickName.trim()) {
      tempErrors.nickName = "Acronym is required.";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateFields()) return;
    if (isContinuing) return;
    setIsContinuing(true);
    try {
      console.log("orgDetails", orgDetails);
  
      console.log("userDetails", userDetails);
      console.log("userDetails._id", userDetails._id);
      const response = await onboardLinkedinOrg({userId: userDetails._id, organization: orgDetails});
      console.log('response', response);
      login(response.user);

      setTimeout(() => {
        navigate(`/subs/org/${response.user.orgId}/projects`);
      }, 1000);
    } catch (error) {
      console.log("error", error);
      showMessage(error?.response?.data?.message || 'Something went wrong', "error");
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 6, px: 2 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" align="center">
          Select Your Organization
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 3, color: "text.secondary" }}>
          Please choose your organization carefully.  
          <strong> Once selected, it cannot be changed later.</strong>
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Make sure you select the correct organization before continuing.
        </Alert>

        <Box sx={{ position: "relative" }} ref={dropdownRef}>
          <TextField
            label="Search or Enter Organization"
            fullWidth
            margin="normal"
            value={orgDetails.organizationName}
            onChange={handleInputChange}
            error={!!errors.organizationName}
            helperText={errors.organizationName}
          />

          {showDropdown && filteredList.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                width: "100%",
                maxHeight: 200,
                overflowY: "auto",
                zIndex: 10,
              }}
            >
              <List>
                {filteredList.map((org) => (
                  <ListItem key={org._id} disablePadding>
                    <ListItemButton onClick={() => handleSelectOrg(org)}>
                      {org.organizationName}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        <TextField
          label="Street"
          fullWidth
          margin="normal"
          value={orgDetails.street}
          onChange={(e) => setOrgDetails({ ...orgDetails, street: e.target.value })}
          error={!!errors.street}
          helperText={errors.street}
        />
        <TextField
          label="City"
          fullWidth
          margin="normal"
          value={orgDetails.city}
          onChange={(e) => setOrgDetails({ ...orgDetails, city: e.target.value })}
          error={!!errors.city}
          helperText={errors.city}
        />
        <TextField
          label="Zip Code"
          fullWidth
          margin="normal"
          value={orgDetails.zip}
          onChange={(e) => setOrgDetails({ ...orgDetails, zip: e.target.value })}
          error={!!errors.zip}
          helperText={errors.zip}
        />
        <TextField
          label="Acronym"
          fullWidth
          margin="normal"
          value={orgDetails.nickName}
          onChange={(e) => setOrgDetails({ ...orgDetails, nickName: e.target.value })}
          error={!!errors.nickName}
          helperText={errors.nickName}
        />

        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            size="large"
            sx={{ px: 5, borderRadius: 2, minWidth: 160 }}
            onClick={handleContinue}
            disabled={isContinuing}
          >
            {isContinuing ? (
              <>
                <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SelectOrganization;
